import React, { useState } from "react";
import { FileType, Image as ImageIcon, Link as LinkIcon, Loader2 } from "lucide-react";
import LoadFailure from "../../components/ui/LoadFailure";
import { useT } from "../../lib/LanguageContext";
import { HQ_SITE } from "../../constants/testIds/hqSite";
import siteApi from "./siteApi";
import useSiteSchema from "./useSiteSchema";
import ContentTab from "./ContentTab";
import MediaTab from "./MediaTab";
import LinksTab from "./LinksTab";
import { siteErrorMessage } from "./siteErrors";
import { useScreenWrites } from "../screenWrites";
import { useSecondaryRead } from "../screenAccess";

/**
 * Super Admin → Site: what the public marketing landing page (/platform) says, shows and
 * links to (docs/hq-site-module/hq-site-api-contract.md). Every save is live on the next
 * render of that page — there is no draft/publish step (contract §2.6).
 *
 * The schema is read once here and handed to every tab: it is the one statement of which
 * fields exist and which limits apply, so no tab restates a rule of its own.
 */
export default function HqSiteManager({ api = siteApi }) {
  const t = useT("superadmin");
  const [tab, setTab] = useState("content");
  const schema = useSiteSchema({ api });
  // Reading the site is data.content.read; every change is settings.write. The media library is
  // its own read (data.media.read), which Support lacks: without it there is no Media tab and
  // the content tab does not ask for the library.
  const { manage: canWrite } = useScreenWrites("site");
  const canReadMedia = useSecondaryRead("siteMedia");
  const readOnly = !canWrite;

  const tabs = [
    { id: "content", label: t("hsite.tab.content"), Icon: FileType },
    ...(canReadMedia ? [{ id: "media", label: t("hsite.tab.media"), Icon: ImageIcon }] : []),
    { id: "links", label: t("hsite.tab.links"), Icon: LinkIcon },
  ];

  return (
    <div data-testid={HQ_SITE.page}>
      <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("hsite.title")}</h1>
      <p className="text-slate-500 mb-6">{t("hsite.subtitle")}</p>
      <div className="flex gap-2 mb-6" role="tablist" aria-label={t("hsite.title")}>
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            data-testid={HQ_SITE.tab(id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm ${tab === id ? "bg-sol-gold/15 text-amber-700 border border-sol-gold/30" : "bg-white text-slate-500 border border-slate-200"}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {schema.status === "loading" && <Loader2 className="w-6 h-6 animate-spin text-amber-600" />}
      {schema.status === "error" && (
        <LoadFailure
          message={siteErrorMessage(schema.error, t)}
          onRetry={schema.reload}
          retryLabel={t("hsite.retry")}
          testId={HQ_SITE.loadError}
          retryTestId={HQ_SITE.loadRetry}
        />
      )}
      {schema.status === "ready" && (
        <div role="tabpanel">
          {tab === "content" && <ContentTab schema={schema.schema} api={api} readOnly={readOnly} canReadMedia={canReadMedia} />}
          {tab === "media" && canReadMedia && <MediaTab schema={schema.schema} api={api} readOnly={readOnly} />}
          {tab === "links" && <LinksTab schema={schema.schema} api={api} readOnly={readOnly} />}
        </div>
      )}
    </div>
  );
}
